import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['list', 'newMessages', 'comment']
  static values = {
    currentUserId: Number,
    unreadIds: Array,
    reportingCycleId: Number,
    commentableType: String,
    markAsReadUrl: String
  }

  connect() {
    this.unreadCount = 0
    this.styleCurrentUserComments()
    this.addUnreadIndicators()
    this.listTarget.addEventListener('scroll', this.handleScroll)

    window.addEventListener('galette:sidebar:close', this.handleSidebarClose)

    // The sidebar loads the content before you actually see it. This will only scroll to the
    // bottom the first time the sidebar is opened
    this.setupVisibilityObserver()
  }

  setupVisibilityObserver = () => {
    this.visibilityObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          this.scrollToBottom()
          this.visibilityObserver.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    this.visibilityObserver.observe(this.element)
  }

  disconnect() {
    if (this.hasListTarget) {
      this.listTarget.removeEventListener('scroll', this.handleScroll)
    }
    window.removeEventListener('galette:sidebar:close', this.handleSidebarClose)

    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
  }

  handleSidebarClose = () => {
    const firstUnreadCard = this.element.querySelector('.reporting-cycle-comments__card--first-unread')
    if (firstUnreadCard) {
      firstUnreadCard.classList.remove('reporting-cycle-comments__card--first-unread')
    }
  }

  handleScroll = () => {
    // If user manually scrolls near bottom, clear the new messages indicator
    if (this.isNearBottom() && this.unreadCount > 0) {
      this.clearUnread()
    }
  }

  styleCurrentUserComments() {
    // Add --current class to comments from the current user on the client side.
    // We do this in JavaScript instead of the server-side component because:
    // 1. During broadcasts (after_create_commit), there's no session/current_person available
    // 2. The broadcast renders the same HTML for all users, so server can't know who is "current"
    // 3. Each client applies the styling based on their own currentUserIdValue
    // This allows broadcasts to work without Devise::MissingWarden errors

    const comments = this.listTarget.querySelectorAll('[data-comments--list-target="comment"]')
    comments.forEach(comment => {
      const authorId = parseInt(comment.dataset.authorId)
      const authorElement = comment.querySelector('.reporting-cycle-comments__author')

      if (authorElement && authorId === this.currentUserIdValue) {
        authorElement.classList.add('reporting-cycle-comments__author--current')
      }
    })
  }

  beforeStreamRender(event) {
    // Capture scroll state before DOM changes
    if (!this.hasListTarget) return

    const stream = event.target
    const targetId = stream?.getAttribute('target')

    if (targetId !== 'reporting-cycle-comments-list') return

    this.lastAction = stream?.getAttribute('action')
    this.wasNearBottom = this.isNearBottom()
    this.beforeScrollHeight = this.listTarget.scrollHeight
    this.beforeScrollTop = this.listTarget.scrollTop

    // Use requestAnimationFrame + setTimeout to ensure DOM has updated
    // RAF ensures we're past the browser paint, then setTimeout ensures stream has rendered
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.handleAfterRender()
      }, 0)
    })
  }

  handleAfterRender() {
    if (!this.lastAction) return

    if (this.lastAction === 'append') {
      const comments = this.listTarget.querySelectorAll('[data-comments--list-target="comment"]')
      const lastComment = comments[comments.length - 1]

      if (!lastComment) return

      const authorId = parseInt(lastComment.dataset.authorId)
      const commentId = parseInt(lastComment.dataset.commentId)

      this.styleCurrentUserComments()

      if (authorId !== this.currentUserIdValue) {
        this.addUnreadDotToComment(lastComment)

        const hasFirstUnread = this.listTarget.querySelector('.reporting-cycle-comments__card--first-unread')
        if (!hasFirstUnread) {
          lastComment.classList.add('reporting-cycle-comments__card--first-unread')
        }
      }

      if (authorId === this.currentUserIdValue || this.wasNearBottom) {
        this.scrollToBottom()
        this.clearUnread()
      } else {
        this.unreadCount += 1
        this.showNewMessagesIndicator()
      }
    }

    this.lastAction = null
  }

  addUnreadDotToComment(comment) {
    const timestampElement = comment.querySelector('.reporting-cycle-comments__timestamp')
    if (timestampElement && !timestampElement.querySelector('.reporting-cycle-comments__unread-indicator')) {
      const dot = document.createElement('span')
      dot.className = 'reporting-cycle-comments__unread-indicator'
      dot.setAttribute('aria-label', 'Unread')
      dot.setAttribute('data-comments--read-tracker-target', 'unreadIndicator')
      timestampElement.appendChild(dot)

      const commentId = parseInt(comment.dataset.commentId)
      comment.setAttribute('data-controller', 'comments--read-tracker')
      comment.setAttribute('data-comments--read-tracker-comment-id-value', commentId)
      comment.setAttribute('data-comments--read-tracker-is-unread-value', 'true')
      comment.setAttribute('data-comments--read-tracker-is-own-comment-value', 'false')
      comment.setAttribute('data-comments--read-tracker-commentable-type-value', this.commentableTypeValue)
      comment.setAttribute('data-comments--read-tracker-commentable-id-value', this.reportingCycleIdValue)
      comment.setAttribute('data-comments--read-tracker-mark-as-read-url-value', this.markAsReadUrlValue)
    }
  }

  isNearBottom() {
    if (!this.hasListTarget) return false
    const threshold = 100 // pixels from bottom
    const { scrollHeight, scrollTop, clientHeight } = this.listTarget
    const distanceFromBottom = scrollHeight - (scrollTop + clientHeight)
    return distanceFromBottom <= threshold
  }

  scrollToBottom() {
    if (this.hasListTarget) {
      this.listTarget.scrollTop = this.listTarget.scrollHeight
    }
  }

  jumpToBottom() {
    this.scrollToBottom()
    this.clearUnread()
  }

  clearUnread() {
    this.unreadCount = 0
    if (this.hasNewMessagesTarget) {
      this.newMessagesTarget.hidden = true
    }
  }

  showNewMessagesIndicator() {
    if (this.hasNewMessagesTarget) {
      this.newMessagesTarget.textContent = `${this.unreadCount} new message${this.unreadCount > 1 ? 's' : ''}`
      this.newMessagesTarget.hidden = false
    }
  }

  addUnreadIndicators() {
    const comments = this.listTarget.querySelectorAll('[data-comments--list-target="comment"]')
    const unreadIds = this.unreadIdsValue || []
    let firstUnreadComment = null

    comments.forEach(comment => {
      const commentId = parseInt(comment.dataset.commentId)
      const authorId = parseInt(comment.dataset.authorId)

      if (unreadIds.includes(commentId) && authorId !== this.currentUserIdValue) {
        this.addUnreadDotToComment(comment)
        if (!firstUnreadComment) {
          firstUnreadComment = comment
        }
      }
    })

    if (firstUnreadComment) {
      firstUnreadComment.classList.add('reporting-cycle-comments__card--first-unread')
    }
  }
}
